/**
 * Cancellation policy engine (KidHive). Defines, validates, and resolves the cancellation policy
 * that a refund is later calculated against. Policies are customer-facing and provider-set, so they
 * live in publicData: a provider default at provider.publicData.cancellationPolicy, optionally
 * overridden per listing at listing.publicData.cancellationPolicy.
 *
 * A policy is { type, tiers } where each tier is { cutoffHours, refundPercentage }: "if cancelled at
 * least cutoffHours before the session start, refund refundPercentage%". Tiers are normalized sorted
 * DESC by cutoffHours; cancelling with less notice than the smallest cutoff yields 0%. The refund
 * EVALUATION (Stripe refunds) is a separate later task -- this is the definition + storage +
 * resolution the refund logic will read.
 *
 * SERVER MIRROR: the booking-time snapshot is attached server-side (it decides refunds, so it must
 * be authoritative and is never trusted from the client). The server (CommonJS) can't import this
 * ESM module, so the minimal resolve/normalize/default it needs is mirrored in
 * server/api-util/cancellationPolicyServer.js. THOSE TWO MUST STAY IN SYNC -- especially
 * SYSTEM_DEFAULT_POLICY and the normalization rules; both are unit-tested against the same fixtures.
 *
 * All functions are pure; mirrors childData.js / providerProfile.js conventions.
 */

import { ensureUser, ensureListing } from './data';
import { showsAsTerm } from './termSchedule';

export const CANCELLATION_POLICY_TYPES = ['flexible', 'moderate', 'strict', 'custom'];

// Sane validation bounds.
export const MAX_CUTOFF_HOURS = 8760; // one year
export const REFUND_PERCENTAGE_MIN = 0;
export const REFUND_PERCENTAGE_MAX = 100;

/**
 * Template tier definitions. Sensible defaults -- a business decision to confirm with the client
 * when the provider-config UI lands; tunable here. cutoffHours: 24 = 1 day, 48 = 2 days, 168 = 7 days.
 */
export const POLICY_TEMPLATES = {
  flexible: [{ cutoffHours: 24, refundPercentage: 100 }],
  // THE platform policy (Cancellation & Refund Policy v1.3 sec.3): 100% at 24h+, 50% at 12-24h,
  // nothing under 12h.
  moderate: [
    { cutoffHours: 24, refundPercentage: 100 },
    { cutoffHours: 12, refundPercentage: 50 },
  ],
  strict: [{ cutoffHours: 168, refundPercentage: 50 }],
};

// The marketplace fallback when neither a listing nor a provider policy is set.
// MUST match SYSTEM_DEFAULT in server/api-util/cancellationPolicyServer.js.
export const SYSTEM_DEFAULT_TYPE = 'moderate';

/**
 * The intl values for one tier line (the shared ClassDetailPage.policyTier* messages). Cutoffs are
 * displayed in DAYS when they are whole days (all template values are: 24/48/168h -> 1/2/7 days --
 * "168 hours" confused users, client decision 2026-07-10), falling back to hours for any custom
 * value that isn't. The zero cutoff renders the "any time / after the start" phrasing via the
 * messages' `anytime` select.
 *
 * @param {Object} intl
 * @param {Object} tier { cutoffHours, refundPercentage }
 */
export const policyTierMessageValues = (intl, tier) => {
  const hours = Number(tier?.cutoffHours) || 0;
  const isDays = hours >= 24 && hours % 24 === 0;
  const duration =
    hours === 0
      ? ''
      : intl.formatMessage(
          { id: isDays ? 'CancellationPolicy.durationDays' : 'CancellationPolicy.durationHours' },
          { count: isDays ? hours / 24 : hours }
        );
  return { anytime: hours === 0 ? 'yes' : 'no', duration, percentage: tier?.refundPercentage };
};

const isPlainObject = value =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const toIntOrNull = value => {
  if (value === '' || value == null) {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(n) ? n : null;
};

// Deep-copy template tiers so callers never mutate the shared template arrays.
const copyTiers = tiers => tiers.map(t => ({ ...t }));

/** Normalize one tier, or null if unusable (out of bounds / non-integer). */
const normalizeTier = tier => {
  if (!isPlainObject(tier)) {
    return null;
  }
  const cutoffHours = toIntOrNull(tier.cutoffHours);
  const refundPercentage = toIntOrNull(tier.refundPercentage);
  if (cutoffHours == null || refundPercentage == null) {
    return null;
  }
  if (cutoffHours < 0 || cutoffHours > MAX_CUTOFF_HOURS) {
    return null;
  }
  if (refundPercentage < REFUND_PERCENTAGE_MIN || refundPercentage > REFUND_PERCENTAGE_MAX) {
    return null;
  }
  return { cutoffHours, refundPercentage };
};

/** True iff a raw value is a policy with at least one usable tier (so it counts as "set"). */
const hasUsableTiers = raw =>
  isPlainObject(raw) && Array.isArray(raw.tiers) && raw.tiers.some(t => normalizeTier(t) != null);

/**
 * Normalize a policy: keep only valid tiers, drop duplicate cutoffHours, sort DESC by cutoffHours.
 * Defensive: an input with no usable tiers yields the system-default (moderate) policy, so a booking
 * is never left with an empty policy.
 * @param {Object} policy
 * @returns {{ type: string, tiers: Array<{cutoffHours:number, refundPercentage:number}> }}
 */
export const normalizeCancellationPolicy = policy => {
  const type =
    isPlainObject(policy) && CANCELLATION_POLICY_TYPES.includes(policy.type) ? policy.type : 'custom';
  const rawTiers = isPlainObject(policy) && Array.isArray(policy.tiers) ? policy.tiers : [];

  const seen = new Set();
  const tiers = rawTiers
    .map(normalizeTier)
    .filter(Boolean)
    .filter(t => {
      if (seen.has(t.cutoffHours)) {
        return false;
      }
      seen.add(t.cutoffHours);
      return true;
    })
    .sort((a, b) => b.cutoffHours - a.cutoffHours);

  if (tiers.length === 0) {
    return { type: SYSTEM_DEFAULT_TYPE, tiers: copyTiers(POLICY_TEMPLATES[SYSTEM_DEFAULT_TYPE]) };
  }
  return { type, tiers };
};

// The marketplace fallback policy object (normalized).
export const SYSTEM_DEFAULT_POLICY = {
  type: SYSTEM_DEFAULT_TYPE,
  tiers: copyTiers(POLICY_TEMPLATES[SYSTEM_DEFAULT_TYPE]),
};

// KidHive -- term-enrollment default. A term is refund-eligible only BEFORE the first session:
// [{ cutoffHours: 0, refundPercentage: 100 }] = full refund any time before the start, 0% after
// (the post-first-session "manual operator" path). Keeps a term booking's snapshot from falling
// through to the single-session moderate default. RATIFIED as the final term policy by the client
// (2026-07-22), and the design rule camps will follow. MUST match TERM_DEFAULT in
// server/api-util/cancellationPolicyServer.js.
export const TERM_DEFAULT_POLICY = {
  type: 'custom',
  tiers: [{ cutoffHours: 0, refundPercentage: 100 }],
};

/**
 * Build a normalized policy from a named template, or null for an unknown template.
 * @param {string} type 'flexible' | 'moderate' | 'strict'
 */
export const buildPolicyFromTemplate = type =>
  POLICY_TEMPLATES[type] ? { type, tiers: copyTiers(POLICY_TEMPLATES[type]) } : null;

/**
 * Build a normalized policy from input: a known template type, or a custom policy with tiers.
 * @param {Object} input { type, tiers? }
 */
export const createCancellationPolicy = (input = {}) => {
  const src = isPlainObject(input) ? input : {};
  if (src.type && src.type !== 'custom' && POLICY_TEMPLATES[src.type]) {
    return buildPolicyFromTemplate(src.type);
  }
  return normalizeCancellationPolicy({ type: 'custom', tiers: src.tiers });
};

/**
 * Validate a policy. Returns field -> error message; empty object means valid.
 * @param {Object} policy
 */
export const validateCancellationPolicy = policy => {
  if (!isPlainObject(policy)) {
    return { _error: 'Policy must be an object' };
  }
  const errors = {};

  if (!CANCELLATION_POLICY_TYPES.includes(policy.type)) {
    errors.type = `Type must be one of: ${CANCELLATION_POLICY_TYPES.join(', ')}`;
  }

  if (!Array.isArray(policy.tiers) || policy.tiers.length === 0) {
    errors.tiers = 'At least one refund tier is required';
    return errors;
  }

  const tierErrors = [];
  policy.tiers.forEach((tier, i) => {
    const e = {};
    const cutoffHours = toIntOrNull(tier?.cutoffHours);
    const refundPercentage = toIntOrNull(tier?.refundPercentage);
    if (cutoffHours == null) {
      e.cutoffHours = 'cutoffHours must be a whole number of hours';
    } else if (cutoffHours < 0 || cutoffHours > MAX_CUTOFF_HOURS) {
      e.cutoffHours = `cutoffHours must be between 0 and ${MAX_CUTOFF_HOURS}`;
    }
    if (refundPercentage == null) {
      e.refundPercentage = 'refundPercentage must be a whole number';
    } else if (refundPercentage < REFUND_PERCENTAGE_MIN || refundPercentage > REFUND_PERCENTAGE_MAX) {
      e.refundPercentage = `refundPercentage must be between ${REFUND_PERCENTAGE_MIN} and ${REFUND_PERCENTAGE_MAX}`;
    }
    if (Object.keys(e).length > 0) {
      tierErrors[i] = e;
    }
  });
  if (tierErrors.length > 0) {
    errors.tiers = tierErrors;
  }

  // Distinct cutoffs.
  const cutoffs = policy.tiers.map(t => t?.cutoffHours);
  if (new Set(cutoffs).size !== cutoffs.length) {
    errors.duplicateCutoff = 'Each tier must use a distinct cutoffHours';
  }

  // Monotonicity: more notice (higher cutoffHours) must not refund less than a shorter notice.
  const sorted = policy.tiers
    .filter(isPlainObject)
    .slice()
    .sort((a, b) => (b.cutoffHours || 0) - (a.cutoffHours || 0));
  for (let i = 1; i < sorted.length; i += 1) {
    if ((sorted[i - 1].refundPercentage || 0) < (sorted[i].refundPercentage || 0)) {
      errors.monotonicity = 'A longer cancellation notice must not refund less than a shorter one';
      break;
    }
  }

  return errors;
};

// ================ Read / resolve ================

/** Read the provider's default policy from publicData (normalized), or null if not set. */
export const getProviderCancellationPolicy = provider => {
  const raw = ensureUser(provider).attributes.profile?.publicData?.cancellationPolicy;
  return hasUsableTiers(raw) ? normalizeCancellationPolicy(raw) : null;
};

/** Read a listing's override policy from publicData (normalized), or null if not set. */
export const getListingCancellationPolicy = listing => {
  const raw = ensureListing(listing).attributes?.publicData?.cancellationPolicy;
  return hasUsableTiers(raw) ? normalizeCancellationPolicy(raw) : null;
};

/**
 * The marketplace fallback policy for a listing, BY CLASS FORMAT: term-enrollment gets the term
 * default; everything else gets the system default (moderate). Reading classFormat here is
 * what keeps a term booking from accidentally snapshotting the single-session default.
 */
const getDefaultPolicyForListing = listing => {
  // A term gets the term default -- except a ONE-session term (the old one-off-class workaround),
  // which parents see as a drop-in and which follows the drop-in tiers (client decision, 2026-10).
  const isTerm = showsAsTerm(ensureListing(listing).attributes?.publicData);
  return normalizeCancellationPolicy(isTerm ? TERM_DEFAULT_POLICY : SYSTEM_DEFAULT_POLICY);
};

/**
 * Resolve the policy that applies to a booking. ONE PLATFORM POLICY (client decision, 2026-07-15):
 * every class uses the per-format system default -- the per-provider/per-listing overrides are no
 * longer consulted (the provider-facing picker is retired; stored publicData.cancellationPolicy
 * values are ignored). Already-booked transactions are unaffected: refunds always use the policy
 * SNAPSHOTTED on the transaction at booking time. Always returns a normalized policy.
 * KEEP IN SYNC with server/api-util/cancellationPolicyServer.js (the authoritative snapshot).
 * @param {Object} opts { listing, provider }
 */
export const resolveCancellationPolicy = ({ listing, provider } = {}) =>
  getDefaultPolicyForListing(listing);
